/**
 * T055: Analytics event tracking utility.
 * Fires events to Shopify App Bridge analytics (if available) and console.
 * FR-034 required events:
 * - onboarding_started
 * - onboarding_step_viewed(step)
 * - onboarding_completed
 * - scan_started(scope, plan_type)
 * - scan_progress_seen
 * - scan_completed(duration, processed_count)
 * - scan_failed(error_code)
 * - email_notify_opted_in
 * - paywall_viewed(source)
 * - upgrade_clicked
 */

export function trackEvent(
  eventName: string,
  payload: Record<string, unknown> = {},
): void {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const shopify = (globalThis as any).shopify;
    if (typeof shopify?.analytics?.track === 'function') {
      shopify.analytics.track(eventName, payload);
    } else {
      console.info(`[Analytics] ${eventName}`, payload);
    }

    // Dispatch CustomEvent for external listeners
    if (typeof globalThis.dispatchEvent === 'function') {
      globalThis.dispatchEvent(
        new CustomEvent('zelium:analytics', {
          detail: { event: eventName, ...payload },
        }),
      );
    }
  } catch {
    // Analytics should never break the UI
  }
}

// ─── Pre-built Event Functions (FR-034) ──────────────────────

export function trackOnboardingStarted(): void {
  trackEvent('onboarding_started');
}

export function trackOnboardingStepViewed(step: number): void {
  trackEvent('onboarding_step_viewed', { step });
}

export function trackOnboardingCompleted(): void {
  trackEvent('onboarding_completed');
}

export function trackScanStarted(scope: string, planType: string): void {
  trackEvent('scan_started', { scope, plan_type: planType });
}

export function trackScanProgressSeen(): void {
  trackEvent('scan_progress_seen');
}

export function trackScanCompleted(duration: number, processedCount: number): void {
  trackEvent('scan_completed', { duration, processed_count: processedCount });
}

export function trackScanFailed(errorCode: string): void {
  trackEvent('scan_failed', { error_code: errorCode });
}

export function trackEmailNotifyOptedIn(): void {
  trackEvent('email_notify_opted_in');
}

export function trackPaywallViewed(source: string): void {
  trackEvent('paywall_viewed', { source });
}

export function trackUpgradeClicked(plan?: string): void {
  trackEvent('upgrade_clicked', { plan });
}
